import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography, ListItem, ListItemText } from '@mui/material';
import { getProjects, getSiteDeparturesForProject, getSiteStockForProject, createSiteDeparture, getMaterialEquipment } from '../services/api';
import DateInputWithArrows from '../components/DateInputWithArrows';
import SiteStockList from '../components/SiteStockList';
import { formatDateEuropean } from '../utils/dateUtils';
import { useTranslation } from 'react-i18next';

function SiteDeparture() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [assignedItems, setAssignedItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialEquipmentId, setMaterialEquipmentId] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [departures, setDepartures] = useState([]);

  useEffect(() => {
    (async () => {
      const p = await getProjects();
      setProjects(p.data || []);
      const m = await getMaterialEquipment({ sort: 'name', order: 'asc' });
      setMaterials(m.data || []);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!projectId) { setAssignedItems([]); setDepartures([]); return; }
      await reloadProjectData(projectId);
    })();
  }, [projectId]);

  const reloadProjectData = async (pid) => {
    const [stockRes, depRes] = await Promise.all([
      getSiteStockForProject(pid),
      getSiteDeparturesForProject(pid),
    ]);
    setDepartures(depRes.data || []);
    setAssignedItems(stockRes.data || []);
  };

  const submit = async () => {
    if (!projectId || !materialEquipmentId || !date || !quantity) return;
    const payload = {
      projectId: Number(projectId),
      materialEquipmentId: Number(materialEquipmentId),
      quantity: Number(quantity),
      departureDate: date,
    };
    if (String(reason || '').trim()) payload.reason = String(reason).trim();
    await createSiteDeparture(payload);
    setQuantity('');
    setMaterialEquipmentId('');
    setReason('');
    await reloadProjectData(projectId);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>{t('siteDeparture.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('siteDeparture.returnHint')}</Typography>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="proj">{t('siteDeparture.project')}</InputLabel>
                <Select labelId="proj" label={t('siteDeparture.project')} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                  {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <DateInputWithArrows fullWidth label={t('siteDeparture.date')} value={date} onChange={(e) => setDate(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="me">{t('siteDeparture.assignedItem')}</InputLabel>
                <Select labelId="me" label={t('siteDeparture.assignedItem')} value={materialEquipmentId} onChange={(e) => setMaterialEquipmentId(e.target.value)}>
                  {assignedItems.map((it) => {
                    const name = it.name || materials.find((me) => Number(me.id) === Number(it.id))?.name || `#${it.id}`;
                    return <MenuItem key={it.id} value={it.id}>{`${name} (${it.quantity})`}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField type="number" label={t('siteDeparture.amount')} inputProps={{ step: 0.01, min: 0 }} fullWidth value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('siteDeparture.reason')}
                fullWidth
                multiline
                minRows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3} display="flex" alignItems="center">
              <Button variant="contained" onClick={submit}>{t('siteDeparture.addDeparture')}</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {projectId && (
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('siteStock.title')}</Typography>
                <SiteStockList items={assignedItems} materials={materials} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('siteDeparture.departures')}</Typography>
                {departures.length === 0 && (
                  <Typography variant="body2" color="text.secondary">{t('siteDeparture.empty')}</Typography>
                )}
                {departures.map((d) => {
                  const materialId = d.materialEquipmentId || d.materialId;
                  const name = d.materialEquipment?.name || materials.find((me) => Number(me.id) === Number(materialId))?.name || `#${materialId}`;
                  return (
                    <ListItem key={d.id} divider disableGutters>
                      <ListItemText
                        primary={`${formatDateEuropean(d.departureDate)} – ${name} – qty: ${d.quantity}`}
                        secondary={d.reason && d.reason !== 'Site departure' ? d.reason : null}
                      />
                    </ListItem>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default SiteDeparture;


